import { VaultsAssetFilter } from '@pages/vaults/components/filters/VaultsAssetFilter'
import type { TChainConfig } from '@pages/vaults/components/filters/VaultsFiltersBar'
import type {
  TFiltersConfig,
  TPendingFiltersState,
  TVaultsFiltersPanelSection
} from '@pages/vaults/components/filters/VaultsFiltersPanel'
import type { TListHead } from '@pages/vaults/components/list/VaultsListHead'
import {
  getVaultChainID,
  getVaultToken,
  getVaultTVL,
  type TKongVault,
  type TKongVaultInput
} from '@pages/vaults/domain/kongVaultSelectors'
import type { TPossibleSortBy } from '@pages/vaults/hooks/useSortVaults'
import {
  getBlockingFilterActionGroups,
  isVaultHiddenByMinTvl,
  MIN_TVL_DISABLED
} from '@pages/vaults/utils/blockingFilterInsights'
import { resolveNextSingleChainSelection } from '@pages/vaults/utils/chainSelection'
import {
  AGGRESSIVENESS_OPTIONS,
  AVAILABLE_TOGGLE_VALUE,
  DEFAULT_MIN_TVL,
  HOLDINGS_TOGGLE_VALUE,
  toggleInArray,
  V2_SUPPORTED_CHAINS,
  V3_ASSET_CATEGORIES,
  V3_DEFAULT_SECONDARY_CHAIN_IDS,
  V3_PRIMARY_CHAIN_IDS,
  V3_SUPPORTED_CHAINS
} from '@pages/vaults/utils/constants'
import { getVaultFeeStructureKey } from '@pages/vaults/utils/vaultFees'
import {
  deriveAssetCategory,
  deriveListKind,
  deriveV3Aggressiveness,
  expandUnderlyingAssetSelection,
  getUnderlyingAssetLabel,
  normalizeUnderlyingAssetSymbol,
  type TVaultAggressiveness
} from '@pages/vaults/utils/vaultListFacets'
import type { TVaultType } from '@pages/vaults/utils/vaultTypeCopy'
import { getSupportedChainsForVaultType } from '@pages/vaults/utils/vaultTypeUtils'
import { useMediaQuery } from '@react-hookz/web'
import type { TMultiSelectOptionProps } from '@shared/components/MultiSelectDropdown'
import { TokenLogo } from '@shared/components/TokenLogo'
import { useWeb3 } from '@shared/contexts/useWeb3'
import { usePrefetchYearnVaults } from '@shared/hooks/useFetchYearnVaults'
import { useOptimisticValue } from '@shared/hooks/useOptimisticValue'
import { getVaultKey } from '@shared/hooks/useVaultFilterUtils'
import type { TSortDirection } from '@shared/types'
import type { RefObject } from 'react'
import {
  type ChangeEvent,
  createElement,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useVaultsListModel } from './useVaultsListModel'
import { useVaultsQueryState } from './useVaultsQueryState'
import { VAULTS_FILTERS_STORAGE_KEY } from './vaultsFiltersStorage'

const DEFAULT_VAULT_TYPES = ['multi', 'single']
const DEFAULT_SORT_BY: TPossibleSortBy = 'tvl'

const areArraysEquivalent = (
  a: Array<string | number> | null | undefined,
  b: Array<string | number> | null | undefined
): boolean => {
  const normalize = (value: Array<string | number> | null | undefined): Array<string | number> => {
    if (!value || value.length === 0) {
      return []
    }
    return [...new Set(value)].sort((left, right) => String(left).localeCompare(String(right)))
  }
  const normalizedA = normalize(a)
  const normalizedB = normalize(b)
  if (normalizedA.length !== normalizedB.length) {
    return false
  }
  return normalizedA.every((value, index) => value === normalizedB[index])
}

type TVaultsPinnedSection = {
  key: string
  vaults: TKongVaultInput[]
}

type TVaultsBlockingFilterBaseActionKey =
  | 'showStrategies'
  | 'showLegacyVaults'
  | 'showAllChains'
  | 'showAllCategories'
  | 'showAllAggressiveness'
  | 'showAllUnderlyingAssets'
  | 'showAllFees'
  | 'clearMinTvl'
  | 'showAllTypes'
  | 'showAllVaults'

export type TVaultsBlockingFilterAction = {
  key: string
  label: string
  additionalResults: number
  onApply: () => void
}

const BLOCKING_FILTER_LABELS: Record<TVaultsBlockingFilterBaseActionKey, string> = {
  showStrategies: 'Show single asset strategies',
  showLegacyVaults: 'Show legacy vaults',
  showAllChains: 'Show all chains',
  showAllCategories: 'Show all categories',
  showAllAggressiveness: 'Show all aggressiveness levels',
  showAllUnderlyingAssets: 'Show all underlying assets',
  showAllFees: 'Show all fee structures',
  clearMinTvl: 'Show low-TVL vaults',
  showAllTypes: 'Show all strategy types',
  showAllVaults: 'Show all vaults'
}

function capitalizeLabel(value: string): string {
  if (value.length === 0) {
    return value
  }
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}

function formatCombinedBlockingFilterLabel(keys: TVaultsBlockingFilterBaseActionKey[]): string {
  const normalizedKeys = Array.from(new Set(keys))
  if (
    normalizedKeys.length === 2 &&
    normalizedKeys.includes('showAllChains') &&
    normalizedKeys.includes('showAllVaults')
  ) {
    return 'Show all chains and vaults'
  }

  const labels = normalizedKeys.map((key) => BLOCKING_FILTER_LABELS[key].toLowerCase())
  if (labels.length === 2) {
    return `${capitalizeLabel(labels[0])} and ${labels[1]}`
  }
  if (labels.length > 2) {
    return `Apply ${labels.slice(0, -1).join(', ')}, and ${labels.at(-1)}`
  }
  return 'Apply suggested filters'
}

type TVaultsFiltersBarModel = {
  search: {
    value: string
    onChange: (value: string) => void
    trailingControls?: React.ReactNode
  }
  filters: {
    count: number
    sections: TVaultsFiltersPanelSection[]
    config: TFiltersConfig
    initialState: TPendingFiltersState
    onApply: (state: TPendingFiltersState) => void
    onClear: () => void
  }
  chains: {
    selected: number[] | null
    onChange: (value: number[] | null) => void
    config: TChainConfig
  }
  shouldStackFilters: boolean
  activeVaultType: TVaultType
  onChangeVaultType: (value: TVaultType) => void
}

type TVaultsListData = {
  isLoading: boolean
  pinnedSections: TVaultsPinnedSection[]
  pinnedVaults: TKongVaultInput[]
  mainVaults: TKongVaultInput[]
  vaultFlags: Record<string, { hasHoldings: boolean; isMigratable: boolean; isRetired: boolean; isHidden: boolean }>
  listChains: number[] | null
  totalMatchingVaults: number
  hiddenByFiltersCount: number
  blockingFilterActions: TVaultsBlockingFilterAction[]
}

type TVaultsListModel = {
  listHeadProps: TListHead
  listVaultType: TVaultType
  shouldCollapseChips: boolean
  displayedShowStrategies: boolean
  activeFilters: {
    activeChains: number[]
    activeCategories: string[]
    activeProductType: 'all' | 'v3' | 'lp'
    activeFeeStructureKey: string | null
  }
  data: TVaultsListData
  handlers: {
    onToggleChain: (chainId: number) => void
    onToggleCategory: (category: string) => void
    onToggleType: (type: string) => void
    onToggleVaultType: (type: 'v3' | 'lp') => void
    onToggleFeeStructure: (feeStructureKey: string) => void
  }
  onResetFilters: () => void
  resolveApyDisplayVariant: (vault: TKongVaultInput) => 'default' | 'factory-list'
}

export type TVaultsPageModel = {
  refs: {
    varsRef: RefObject<HTMLDivElement | null>
    filtersRef: RefObject<HTMLDivElement | null>
  }
  header: {
    vaultType: TVaultType
    suggestedVaults: TKongVault[]
  }
  filtersBar: TVaultsFiltersBarModel
  list: TVaultsListModel
}

export function useVaultsPageModel(): TVaultsPageModel {
  const { address } = useWeb3()
  const hasWalletAddress = !!address
  const {
    vaultType,
    hasTypesParam,
    search,
    types,
    categories,
    chains,
    aggressiveness,
    underlyingAssets,
    minTvl,
    showLegacyVaults,
    showStrategies,
    onSearch,
    onChangeTypes,
    onChangeCategories,
    onChangeChains,
    onChangeAggressiveness,
    onChangeUnderlyingAssets,
    onChangeMinTvl,
    onChangeShowLegacyVaults,
    onChangeShowStrategies,
    onChangeVaultType,
    onChangeSortBy,
    onChangeSortDirection,
    onResetMultiSelect,
    onResetExtraFilters,
    sortBy,
    sortDirection
  } = useVaultsQueryState({
    defaultTypes: DEFAULT_VAULT_TYPES,
    defaultCategories: [],
    defaultPathname: '/vaults',
    defaultSortBy: DEFAULT_SORT_BY,
    resetTypes: DEFAULT_VAULT_TYPES,
    resetCategories: [],
    persistToStorage: true,
    storageKey: VAULTS_FILTERS_STORAGE_KEY,
    clearUrlAfterInit: false,
    shareUpdatesUrl: true,
    syncUrlOnChange: true
  })

  usePrefetchYearnVaults(vaultType === 'v3')

  const varsRef = useRef<HTMLDivElement | null>(null)
  const filtersRef = useRef<HTMLDivElement | null>(null)
  const searchValue = search ?? ''
  const listVaultType = useDeferredValue(vaultType)
  const isBelow1000 =
    useMediaQuery('(max-width: 1000px)', {
      initializeWithValue: false
    }) ?? false
  const isBelow768 =
    useMediaQuery('(max-width: 767px)', {
      initializeWithValue: false
    }) ?? false
  const shouldCollapseChips = isBelow1000
  const shouldStackFilters = isBelow1000 && !isBelow768

  // Optimistic UI — show immediate feedback while URL state catches up
  const [displayedVaultType, setOptimisticVaultType] = useOptimisticValue(vaultType)
  const [displayedChains, setOptimisticChains] = useOptimisticValue(chains, areArraysEquivalent)
  const [displayedTypes, setOptimisticTypes] = useOptimisticValue(types, areArraysEquivalent)
  const [displayedCategories, setOptimisticCategories] = useOptimisticValue(categories, areArraysEquivalent)
  const [displayedAggressiveness, setOptimisticAggressiveness] = useOptimisticValue(aggressiveness, areArraysEquivalent)
  const [displayedUnderlyingAssets, setOptimisticUnderlyingAssets] = useOptimisticValue(
    underlyingAssets,
    areArraysEquivalent
  )
  const [displayedMinTvl, setOptimisticMinTvl] = useOptimisticValue(minTvl)
  const [displayedShowLegacyVaults, setOptimisticShowLegacyVaults] = useOptimisticValue(showLegacyVaults)
  const displayedShowHiddenVaults = false
  const [displayedShowStrategies, setOptimisticShowStrategies] = useOptimisticValue(showStrategies)
  const [activeFeeStructureKey, setActiveFeeStructureKey] = useState<string | null>(null)

  const listChains = useDeferredValue(chains)
  const listTypes = useDeferredValue(types)
  const listCategories = useDeferredValue(categories)
  const listAggressiveness = useDeferredValue(aggressiveness)
  const listUnderlyingAssets = useDeferredValue(underlyingAssets)
  const listFeeStructureKey = useDeferredValue(activeFeeStructureKey)
  const listMinTvl = useDeferredValue(minTvl)
  const listShowLegacyVaults = useDeferredValue(showLegacyVaults)
  const listShowHiddenVaults = false
  const listShowStrategies = useDeferredValue(showStrategies)
  const hasDisplayedTypesParam = hasTypesParam || displayedTypes !== types
  const hasListTypesParam = hasTypesParam

  const resolveV3Types = useCallback(
    (selected: string[] | null | undefined, shouldShowStrategies: boolean, hasTypesParam: boolean): string[] => {
      const filtered = (selected || []).filter((type) => type === 'multi' || type === 'single')
      if (!shouldShowStrategies) {
        return ['multi']
      }
      if (!hasTypesParam || filtered.length === 0) {
        return ['multi', 'single']
      }
      return filtered
    },
    []
  )

  const displayedV3Types = useMemo(
    () => resolveV3Types(displayedTypes, displayedShowStrategies, hasDisplayedTypesParam),
    [displayedTypes, displayedShowStrategies, hasDisplayedTypesParam, resolveV3Types]
  )
  const listV3Types = useMemo(
    () => resolveV3Types(listTypes, listShowStrategies, hasListTypesParam),
    [listTypes, listShowStrategies, hasListTypesParam, resolveV3Types]
  )
  const displayedCategoriesSanitized = useMemo(() => {
    const allowed = V3_ASSET_CATEGORIES
    return (displayedCategories || []).filter((value) => allowed.includes(value))
  }, [displayedCategories])

  const displayedAggressivenessSanitized = useMemo(() => {
    const allowed = new Set(AGGRESSIVENESS_OPTIONS)
    return (displayedAggressiveness || []).filter((value): value is TVaultAggressiveness =>
      allowed.has(value as TVaultAggressiveness)
    )
  }, [displayedAggressiveness])

  const listAggressivenessSanitized = useMemo(() => {
    const allowed = new Set(AGGRESSIVENESS_OPTIONS)
    return (listAggressiveness || []).filter((value): value is TVaultAggressiveness =>
      allowed.has(value as TVaultAggressiveness)
    )
  }, [listAggressiveness])

  const displayedUnderlyingAssetsSanitized = useMemo(() => {
    const normalized = (displayedUnderlyingAssets || [])
      .map((asset) => normalizeUnderlyingAssetSymbol(asset))
      .filter(Boolean)
    return Array.from(new Set(normalized))
  }, [displayedUnderlyingAssets])

  const listUnderlyingAssetsSanitized = useMemo(() => {
    const normalized = (listUnderlyingAssets || [])
      .map((asset) => normalizeUnderlyingAssetSymbol(asset))
      .filter(Boolean)
    return Array.from(new Set(normalized))
  }, [listUnderlyingAssets])
  const [activeToggleValues, setActiveToggleValues] = useState<string[]>([])
  const [holdingsPinnedSortDirection, setHoldingsPinnedSortDirection] = useState<TSortDirection>('')
  const effectiveSortBy = sortBy === 'featuringScore' ? DEFAULT_SORT_BY : sortBy
  const effectiveSortDirection = sortBy === 'featuringScore' ? 'desc' : sortDirection
  const isHoldingsPinned = activeToggleValues.includes(HOLDINGS_TOGGLE_VALUE)
  const isAvailablePinned = activeToggleValues.includes(AVAILABLE_TOGGLE_VALUE)
  const {
    listCategoriesSanitized,
    holdingsVaults,
    availableVaults,
    vaultFlags,
    underlyingAssetVaults,
    pinnedSections,
    pinnedVaults,
    mainVaults,
    suggestedVaults,
    totalMatchingVaults,
    totalHoldingsMatching,
    isLoadingVaultList
  } = useVaultsListModel({
    listVaultType,
    listChains,
    listV3Types,
    listCategories,
    listAggressiveness,
    listUnderlyingAssets: listUnderlyingAssetsSanitized,
    listFeeStructureKey,
    listMinTvl,
    listShowLegacyVaults,
    listShowHiddenVaults,
    searchValue,
    sortBy: effectiveSortBy,
    sortDirection: effectiveSortDirection,
    holdingsPinnedSortDirection,
    isHoldingsPinned,
    isAvailablePinned
  })

  const shouldComputeBlockingInsights = searchValue !== ''
  const isTypesFilterBlockingResults =
    listVaultType !== 'factory' &&
    listShowStrategies &&
    hasListTypesParam &&
    !areArraysEquivalent(listV3Types, DEFAULT_VAULT_TYPES)
  const listUnderlyingAssetsExpanded = useMemo(
    () => expandUnderlyingAssetSelection(listUnderlyingAssetsSanitized),
    [listUnderlyingAssetsSanitized]
  )
  const activeV3Kinds = useMemo(() => {
    const kinds = new Set<'allocator' | 'strategy'>()
    if (listV3Types.includes('multi')) {
      kinds.add('allocator')
    }
    if (listV3Types.includes('single')) {
      kinds.add('strategy')
    }
    return kinds
  }, [listV3Types])
  const blockingProbeSortDirection: TSortDirection = ''
  const blockingProbeBaseArgs: Parameters<typeof useVaultsListModel>[0] = {
    enabled: shouldComputeBlockingInsights,
    listVaultType,
    listChains,
    listV3Types,
    listCategories,
    listAggressiveness: listAggressivenessSanitized.length > 0 ? listAggressivenessSanitized : null,
    listUnderlyingAssets: listUnderlyingAssetsSanitized,
    listFeeStructureKey,
    listMinTvl,
    listShowLegacyVaults,
    listShowHiddenVaults,
    searchValue,
    sortBy: effectiveSortBy,
    sortDirection: blockingProbeSortDirection,
    holdingsPinnedSortDirection: blockingProbeSortDirection,
    isHoldingsPinned: false,
    isAvailablePinned: false
  }

  const { pinnedVaults: allBlockingFiltersPinnedVaults, mainVaults: allBlockingFiltersMainVaults } = useVaultsListModel(
    {
      ...blockingProbeBaseArgs,
      listVaultType: 'all',
      listChains: null,
      listV3Types: DEFAULT_VAULT_TYPES,
      listCategories: null,
      listAggressiveness: null,
      listUnderlyingAssets: null,
      listFeeStructureKey: null,
      listMinTvl: MIN_TVL_DISABLED,
      listShowLegacyVaults: true,
      listShowHiddenVaults: false
    }
  )

  const currentVisibleVaultKeys = useMemo(() => {
    return new Set([...pinnedVaults, ...mainVaults].map((vault) => getVaultKey(vault)))
  }, [mainVaults, pinnedVaults])

  const allBlockingFiltersVaults = useMemo(
    () => [...allBlockingFiltersPinnedVaults, ...allBlockingFiltersMainVaults],
    [allBlockingFiltersMainVaults, allBlockingFiltersPinnedVaults]
  )

  const hiddenByFiltersVaults = useMemo(() => {
    return allBlockingFiltersVaults.filter((vault) => !currentVisibleVaultKeys.has(getVaultKey(vault)))
  }, [allBlockingFiltersVaults, currentVisibleVaultKeys])

  const getBlockingFilterKeysForVault = useCallback(
    (vault: TKongVaultInput): TVaultsBlockingFilterBaseActionKey[] => {
      const blockingKeys = new Set<TVaultsBlockingFilterBaseActionKey>()
      const listKind = deriveListKind(vault)
      const isV3Kind = listKind === 'allocator' || listKind === 'strategy'
      const isV2Kind = listKind === 'factory' || listKind === 'legacy'

      if ((listVaultType === 'v3' && isV2Kind) || (listVaultType === 'factory' && isV3Kind)) {
        blockingKeys.add('showAllVaults')
      }

      if (!listShowLegacyVaults && listKind === 'legacy') {
        blockingKeys.add('showLegacyVaults')
      }
      if (listChains !== null && !listChains.includes(getVaultChainID(vault))) {
        blockingKeys.add('showAllChains')
      }

      if (listCategoriesSanitized.length > 0) {
        const vaultCategory = deriveAssetCategory(vault)
        if (!listCategoriesSanitized.includes(vaultCategory)) {
          blockingKeys.add('showAllCategories')
        }
      }

      if (listAggressivenessSanitized.length > 0) {
        const vaultAggressiveness = deriveV3Aggressiveness(vault)
        if (!vaultAggressiveness || !listAggressivenessSanitized.includes(vaultAggressiveness)) {
          blockingKeys.add('showAllAggressiveness')
        }
      }

      if (listUnderlyingAssetsExpanded.size > 0) {
        const assetKey = normalizeUnderlyingAssetSymbol(getVaultToken(vault).symbol)
        if (!assetKey || !listUnderlyingAssetsExpanded.has(assetKey)) {
          blockingKeys.add('showAllUnderlyingAssets')
        }
      }

      if (listFeeStructureKey && getVaultFeeStructureKey(vault) !== listFeeStructureKey) {
        blockingKeys.add('showAllFees')
      }

      const tvl = getVaultTVL(vault).tvl || 0
      if (isVaultHiddenByMinTvl({ minTvl: listMinTvl, vaultTvl: tvl })) {
        blockingKeys.add('clearMinTvl')
      }

      if (isV3Kind) {
        if (!listShowStrategies && listKind === 'strategy') {
          blockingKeys.add('showStrategies')
        }
        if (isTypesFilterBlockingResults && !activeV3Kinds.has(listKind)) {
          blockingKeys.add('showAllTypes')
        }
      }

      return Array.from(blockingKeys)
    },
    [
      activeV3Kinds,
      isTypesFilterBlockingResults,
      listAggressivenessSanitized,
      listCategoriesSanitized,
      listChains,
      listFeeStructureKey,
      listMinTvl,
      listShowHiddenVaults,
      listShowLegacyVaults,
      listShowStrategies,
      listUnderlyingAssetsExpanded,
      listVaultType
    ]
  )

  const hiddenByFiltersBlockingKeys = useMemo((): TVaultsBlockingFilterBaseActionKey[][] => {
    if (!shouldComputeBlockingInsights || hiddenByFiltersVaults.length === 0) {
      return []
    }
    return hiddenByFiltersVaults.map((vault) => getBlockingFilterKeysForVault(vault))
  }, [getBlockingFilterKeysForVault, hiddenByFiltersVaults, shouldComputeBlockingInsights])

  const blockingFilterActionGroups = useMemo(
    () => getBlockingFilterActionGroups(hiddenByFiltersBlockingKeys),
    [hiddenByFiltersBlockingKeys]
  )

  useEffect(() => {
    if (holdingsVaults.length === 0 && isHoldingsPinned) {
      setHoldingsPinnedSortDirection('')
      setActiveToggleValues((prev) => prev.filter((value) => value !== HOLDINGS_TOGGLE_VALUE))
    }
  }, [holdingsVaults.length, isHoldingsPinned])

  useEffect(() => {
    if (availableVaults.length === 0 && isAvailablePinned) {
      setActiveToggleValues((prev) => prev.filter((value) => value !== AVAILABLE_TOGGLE_VALUE))
    }
  }, [availableVaults.length, isAvailablePinned])

  const filtersCount = useMemo(() => {
    const typeCount = displayedV3Types.includes('single') ? 1 : 0
    const legacyCount = displayedShowLegacyVaults ? 1 : 0
    const hiddenCount = displayedShowHiddenVaults ? 1 : 0
    const categoryCount = displayedCategoriesSanitized.length
    const aggressivenessCount = displayedAggressivenessSanitized.length
    const underlyingAssetCount = displayedUnderlyingAssetsSanitized.length
    const minTvlCount = displayedMinTvl !== DEFAULT_MIN_TVL ? 1 : 0
    return (
      typeCount + legacyCount + hiddenCount + categoryCount + aggressivenessCount + underlyingAssetCount + minTvlCount
    )
  }, [
    displayedAggressivenessSanitized.length,
    displayedCategoriesSanitized.length,
    displayedShowHiddenVaults,
    displayedShowLegacyVaults,
    displayedUnderlyingAssetsSanitized.length,
    displayedMinTvl,
    displayedV3Types
  ])
  const activeChains = useMemo(() => displayedChains ?? [], [displayedChains])
  const activeCategories = displayedCategoriesSanitized
  const activeProductType = useMemo<'all' | 'v3' | 'lp'>(
    () => (displayedVaultType === 'factory' ? 'lp' : displayedVaultType),
    [displayedVaultType]
  )
  const resolveApyDisplayVariant = useCallback((vault: TKongVaultInput): 'default' | 'factory-list' => {
    const listKind = deriveListKind(vault)
    return listKind === 'allocator' || listKind === 'strategy' ? 'default' : 'factory-list'
  }, [])
  const handleChainsChange = useCallback(
    (nextChains: number[] | null): void => {
      const normalizedChains = nextChains ?? []
      setOptimisticChains(normalizedChains)
      onChangeChains(nextChains)
    },
    [onChangeChains]
  )
  const handleTypesChange = useCallback(
    (nextTypes: string[] | null): void => {
      const normalizedTypes = nextTypes ?? []
      setOptimisticTypes(normalizedTypes)
      onChangeTypes(nextTypes)
    },
    [onChangeTypes]
  )
  const handleCategoriesChange = useCallback(
    (nextCategories: string[] | null): void => {
      const normalizedCategories = nextCategories ?? []
      setOptimisticCategories(normalizedCategories)
      onChangeCategories(nextCategories)
    },
    [onChangeCategories]
  )
  const handleAggressivenessChange = useCallback(
    (nextAggressiveness: string[] | null): void => {
      const normalizedAggressiveness = nextAggressiveness ?? []
      setOptimisticAggressiveness(normalizedAggressiveness)
      onChangeAggressiveness(nextAggressiveness)
    },
    [onChangeAggressiveness]
  )
  const handleUnderlyingAssetsChange = useCallback(
    (nextAssets: string[] | null): void => {
      const normalizedAssets = nextAssets ?? []
      setOptimisticUnderlyingAssets(normalizedAssets)
      onChangeUnderlyingAssets(nextAssets)
    },
    [onChangeUnderlyingAssets]
  )
  const handleMinTvlChange = useCallback(
    (nextValue: number): void => {
      const normalizedValue = Number.isFinite(nextValue) ? Math.max(0, nextValue) : DEFAULT_MIN_TVL
      setOptimisticMinTvl(normalizedValue)
      onChangeMinTvl(normalizedValue)
    },
    [onChangeMinTvl]
  )
  const handleShowLegacyVaultsChange = useCallback(
    (nextValue: boolean): void => {
      setOptimisticShowLegacyVaults(nextValue)
      onChangeShowLegacyVaults(nextValue)
    },
    [onChangeShowLegacyVaults]
  )
  const handleShowStrategiesChange = useCallback(
    (nextValue: boolean): void => {
      setOptimisticShowStrategies(nextValue)
      onChangeShowStrategies(nextValue)
    },
    [onChangeShowStrategies]
  )
  const handleToggleChain = useCallback(
    (chainId: number): void => {
      handleChainsChange(resolveNextSingleChainSelection(displayedChains, chainId))
    },
    [displayedChains, handleChainsChange]
  )
  const handleToggleCategory = useCallback(
    (category: string): void => {
      if (displayedCategoriesSanitized.includes(category)) {
        handleCategoriesChange([])
        return
      }
      handleCategoriesChange([category])
    },
    [displayedCategoriesSanitized, handleCategoriesChange]
  )
  const handleToggleType = useCallback(
    (type: string): void => {
      if (displayedVaultType !== 'v3') {
        return
      }
      handleTypesChange(toggleInArray(displayedV3Types, type))
    },
    [displayedVaultType, displayedV3Types, handleTypesChange]
  )
  const handleToggleFeeStructure = useCallback((feeStructureKey: string): void => {
    setActiveFeeStructureKey((current) => (current === feeStructureKey ? null : feeStructureKey))
  }, [])

  const underlyingAssetOptions = useMemo((): TMultiSelectOptionProps[] => {
    const selectedAssets = new Set(displayedUnderlyingAssetsSanitized)
    const options: TMultiSelectOptionProps[] = Object.entries(underlyingAssetVaults).map(([assetKey, vault]) => {
      const label = getUnderlyingAssetLabel(assetKey)
      const token = getVaultToken(vault)
      const tokenAddress = token.address.toLowerCase()
      const tokenLogoSrc = `${import.meta.env.VITE_BASE_YEARN_ASSETS_URI}/tokens/${getVaultChainID(vault)}/${tokenAddress}/logo-32.png`
      return {
        label,
        value: assetKey,
        isSelected: selectedAssets.has(assetKey),
        icon: createElement(TokenLogo, {
          src: tokenLogoSrc,
          tokenSymbol: token.symbol,
          width: 20,
          height: 20
        })
      }
    })
    for (const assetKey of selectedAssets) {
      if (options.some((option) => option.value === assetKey)) {
        continue
      }
      options.push({
        label: getUnderlyingAssetLabel(assetKey),
        value: assetKey,
        isSelected: true
      })
    }

    return options.sort((left, right) => left.label.localeCompare(right.label))
  }, [displayedUnderlyingAssetsSanitized, underlyingAssetVaults])

  const handleUnderlyingAssetsSelect = useCallback(
    (options: TMultiSelectOptionProps[]): void => {
      const selected = options.filter((option) => option.isSelected).map((option) => String(option.value))
      const isAllSelected = options.length > 0 && selected.length === options.length
      const nextSelection = selected.length > 0 ? selected : null
      handleUnderlyingAssetsChange(isAllSelected ? null : nextSelection)
    },
    [handleUnderlyingAssetsChange]
  )

  const handleVaultVersionToggle = useCallback(
    (nextType: TVaultType): void => {
      if (nextType === displayedVaultType) {
        return
      }
      setOptimisticVaultType(nextType)
      onChangeVaultType(nextType)
    },
    [displayedVaultType, onChangeVaultType]
  )
  const handleToggleVaultType = useCallback(
    (nextType: 'v3' | 'lp'): void => {
      const isActive = activeProductType === nextType
      if (isActive) {
        handleVaultVersionToggle('all')
        return
      }
      const targetType = nextType === 'lp' ? 'factory' : 'v3'
      handleVaultVersionToggle(targetType)
    },
    [activeProductType, handleVaultVersionToggle]
  )

  const handleResetFilters = useCallback((): void => {
    setOptimisticChains([])
    setOptimisticCategories([])
    setOptimisticAggressiveness([])
    setOptimisticUnderlyingAssets([])
    setActiveFeeStructureKey(null)
    setOptimisticMinTvl(DEFAULT_MIN_TVL)
    setOptimisticTypes(DEFAULT_VAULT_TYPES)
    setOptimisticShowLegacyVaults(false)
    setOptimisticShowStrategies(false)
    onResetMultiSelect()
    onResetExtraFilters()
  }, [onResetExtraFilters, onResetMultiSelect])

  const handleEnableShowStrategies = useCallback((): void => {
    setOptimisticShowStrategies(true)
    onChangeShowStrategies(true)
  }, [onChangeShowStrategies])

  const handleEnableShowLegacyVaults = useCallback((): void => {
    setOptimisticShowLegacyVaults(true)
    onChangeShowLegacyVaults(true)
  }, [onChangeShowLegacyVaults])

  const handleShowAllChains = useCallback((): void => {
    handleChainsChange(null)
  }, [handleChainsChange])

  const handleShowAllCategories = useCallback((): void => {
    handleCategoriesChange(null)
  }, [handleCategoriesChange])

  const handleShowAllAggressiveness = useCallback((): void => {
    handleAggressivenessChange(null)
  }, [handleAggressivenessChange])

  const handleShowAllUnderlyingAssets = useCallback((): void => {
    handleUnderlyingAssetsChange(null)
  }, [handleUnderlyingAssetsChange])

  const handleShowAllFees = useCallback((): void => {
    setActiveFeeStructureKey(null)
  }, [])

  const handleClearMinTvl = useCallback((): void => {
    handleMinTvlChange(MIN_TVL_DISABLED)
  }, [handleMinTvlChange])

  const handleShowAllTypes = useCallback((): void => {
    handleTypesChange(DEFAULT_VAULT_TYPES)
  }, [handleTypesChange])

  const handleShowAllVaults = useCallback((): void => {
    handleVaultVersionToggle('all')
  }, [handleVaultVersionToggle])

  const hiddenByFiltersCount = hiddenByFiltersVaults.length

  const blockingFilterActions = useMemo((): TVaultsBlockingFilterAction[] => {
    const actionHandlers: Record<TVaultsBlockingFilterBaseActionKey, () => void> = {
      showStrategies: handleEnableShowStrategies,
      showLegacyVaults: handleEnableShowLegacyVaults,
      showAllChains: handleShowAllChains,
      showAllCategories: handleShowAllCategories,
      showAllAggressiveness: handleShowAllAggressiveness,
      showAllUnderlyingAssets: handleShowAllUnderlyingAssets,
      showAllFees: handleShowAllFees,
      clearMinTvl: handleClearMinTvl,
      showAllTypes: handleShowAllTypes,
      showAllVaults: handleShowAllVaults
    }

    return blockingFilterActionGroups.map(({ keys, additionalResults }): TVaultsBlockingFilterAction => {
      if (keys.length === 1) {
        const [key] = keys
        return {
          key,
          label: BLOCKING_FILTER_LABELS[key],
          additionalResults,
          onApply: actionHandlers[key]
        }
      }

      return {
        key: `combo:${keys.join('+')}`,
        label: formatCombinedBlockingFilterLabel(keys),
        additionalResults,
        onApply: (): void => {
          for (const key of keys) {
            actionHandlers[key]()
          }
        }
      }
    })
  }, [
    blockingFilterActionGroups,
    handleClearMinTvl,
    handleEnableShowLegacyVaults,
    handleEnableShowStrategies,
    handleShowAllAggressiveness,
    handleShowAllCategories,
    handleShowAllChains,
    handleShowAllFees,
    handleShowAllTypes,
    handleShowAllUnderlyingAssets,
    handleShowAllVaults
  ])

  const minTvlInput = createElement(
    'div',
    { className: 'flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2' },
    createElement('span', { className: 'text-sm text-text-secondary' }, '$'),
    createElement('input', {
      type: 'number',
      min: 0,
      step: 1,
      value: displayedMinTvl,
      onChange: (event: ChangeEvent<HTMLInputElement>): void => handleMinTvlChange(Number(event.target.value)),
      className: 'w-full bg-transparent text-sm text-text-primary outline-hidden',
      'aria-label': 'Minimum TVL'
    })
  )

  const filtersSections: TVaultsFiltersPanelSection[] = [
    {
      type: 'custom',
      title: 'Underlying Asset',
      content: createElement(VaultsAssetFilter, {
        options: underlyingAssetOptions,
        onSelect: handleUnderlyingAssetsSelect,
        buttonLabel: 'Filter by assets'
      })
    },
    {
      type: 'custom',
      title: 'Minimum TVL',
      content: minTvlInput
    },
    {
      type: 'checklist',
      title: 'Asset Category',
      options: V3_ASSET_CATEGORIES.map((value) => ({
        label: value,
        checked: displayedCategoriesSanitized.includes(value),
        onToggle: (): void => handleCategoriesChange(toggleInArray(displayedCategoriesSanitized, value))
      }))
    },
    {
      type: 'checklist',
      title: 'Vault Aggressiveness',
      options: AGGRESSIVENESS_OPTIONS.map((value) => ({
        label: value,
        checked: displayedAggressivenessSanitized.includes(value),
        onToggle: (): void => handleAggressivenessChange(toggleInArray(displayedAggressivenessSanitized, value))
      }))
    },
    {
      type: 'advanced',
      title: 'Advanced',
      toggles: [
        {
          label: 'Show single asset strategies',
          description: 'Checking this will show the underlying strategies used in Single Asset Vaults in the list.',
          checked: displayedShowStrategies,
          onChange: (checked: boolean): void => handleShowStrategiesChange(checked)
        },
        {
          label: 'Show legacy vaults',
          description: 'Includes legacy vaults in the list.',
          checked: displayedShowLegacyVaults,
          onChange: (checked: boolean): void => handleShowLegacyVaultsChange(checked)
        }
      ]
    }
  ]

  const filtersConfig = useMemo(
    (): TFiltersConfig => ({
      categoryOptions: V3_ASSET_CATEGORIES,
      aggressivenessOptions: AGGRESSIVENESS_OPTIONS,
      toggleOptions: [
        {
          key: 'showStrategies',
          label: 'Show single asset strategies',
          description: 'Checking this will show the underlying strategies used in Single Asset Vaults in the list.'
        },
        {
          key: 'showLegacyVaults',
          label: 'Show legacy vaults',
          description: 'Includes legacy vaults in the list.'
        }
      ],
      underlyingAssetOptions,
      minTvlEnabled: true
    }),
    [underlyingAssetOptions]
  )

  const filtersInitialState = useMemo(
    (): TPendingFiltersState => ({
      categories: displayedCategoriesSanitized,
      aggressiveness: displayedAggressivenessSanitized,
      underlyingAssets: displayedUnderlyingAssetsSanitized,
      minTvl: displayedMinTvl,
      showStrategies: displayedShowStrategies,
      showLegacyVaults: displayedShowLegacyVaults
    }),
    [
      displayedCategoriesSanitized,
      displayedAggressivenessSanitized,
      displayedUnderlyingAssetsSanitized,
      displayedMinTvl,
      displayedShowStrategies,
      displayedShowLegacyVaults
    ]
  )

  const onApplyFilters = useCallback(
    (state: TPendingFiltersState): void => {
      setOptimisticCategories(state.categories)
      setOptimisticAggressiveness(state.aggressiveness)
      setOptimisticUnderlyingAssets(state.underlyingAssets)
      setOptimisticMinTvl(state.minTvl)
      setOptimisticShowStrategies(state.showStrategies)
      setOptimisticShowLegacyVaults(state.showLegacyVaults)

      onChangeCategories(state.categories.length > 0 ? state.categories : null)
      onChangeAggressiveness(state.aggressiveness.length > 0 ? state.aggressiveness : null)
      onChangeUnderlyingAssets(state.underlyingAssets.length > 0 ? state.underlyingAssets : null)
      onChangeMinTvl(state.minTvl)
      onChangeShowStrategies(state.showStrategies)
      onChangeShowLegacyVaults(state.showLegacyVaults)
    },
    [
      onChangeCategories,
      onChangeAggressiveness,
      onChangeUnderlyingAssets,
      onChangeMinTvl,
      onChangeShowStrategies,
      onChangeShowLegacyVaults
    ]
  )

  const chainConfig = useMemo((): TChainConfig => {
    if (listVaultType === 'v3') {
      return {
        supportedChainIds: V3_SUPPORTED_CHAINS,
        primaryChainIds: V3_PRIMARY_CHAIN_IDS,
        defaultSecondaryChainIds: V3_DEFAULT_SECONDARY_CHAIN_IDS,
        chainDisplayOrder: V3_SUPPORTED_CHAINS,
        showMoreChainsButton: false,
        allChainsLabel: 'All Chains'
      }
    }
    if (listVaultType === 'all') {
      const allChains = getSupportedChainsForVaultType('all')
      return {
        supportedChainIds: allChains,
        primaryChainIds: allChains,
        defaultSecondaryChainIds: [],
        chainDisplayOrder: allChains,
        showMoreChainsButton: false,
        allChainsLabel: 'All Chains'
      }
    }
    return {
      supportedChainIds: V2_SUPPORTED_CHAINS,
      primaryChainIds: V2_SUPPORTED_CHAINS,
      defaultSecondaryChainIds: [],
      chainDisplayOrder: V2_SUPPORTED_CHAINS,
      showMoreChainsButton: false,
      allChainsLabel: 'All Chains'
    }
  }, [listVaultType])

  const listHeadProps: TListHead = {
    containerClassName: 'rounded-t-xl bg-surface shrink-0',
    wrapperClassName: 'relative z-10 border border-border rounded-t-lg bg-transparent',
    sortBy: effectiveSortBy,
    sortDirection: effectiveSortDirection,
    onSort: (newSortBy: string, newSortDirection: TSortDirection): void => {
      let targetSortBy = newSortBy as TPossibleSortBy
      let targetSortDirection = newSortDirection as TSortDirection

      if (targetSortBy === 'deposited' && totalHoldingsMatching === 0) {
        targetSortBy = DEFAULT_SORT_BY
        targetSortDirection = 'desc'
      }

      onChangeSortBy(targetSortBy)
      onChangeSortDirection(targetSortDirection)
    },
    onToggle: (value): void => {
      if (value === HOLDINGS_TOGGLE_VALUE) {
        const isHoldingsActive = activeToggleValues.includes(HOLDINGS_TOGGLE_VALUE)
        if (!isHoldingsActive) {
          setHoldingsPinnedSortDirection('')
          setActiveToggleValues([HOLDINGS_TOGGLE_VALUE])
          return
        }
        if (holdingsPinnedSortDirection === '') {
          setHoldingsPinnedSortDirection('desc')
          return
        }
        if (holdingsPinnedSortDirection === 'desc') {
          setHoldingsPinnedSortDirection('asc')
          return
        }
        setHoldingsPinnedSortDirection('')
        setActiveToggleValues((prev) => prev.filter((entry) => entry !== value))
        return
      }

      setHoldingsPinnedSortDirection('')
      setActiveToggleValues((prev) => {
        if (prev.includes(value)) {
          return prev.filter((entry) => entry !== value)
        }
        return [value]
      })
    },
    activeToggleValues,
    items: [
      {
        type: 'sort',
        label: 'Vault',
        value: 'vault',
        sortable: false,
        className: 'col-span-12'
      },
      {
        type: 'sort',
        label: 'Est. APY',
        value: 'estAPY',
        sortable: true,
        className: hasWalletAddress ? 'col-span-4' : 'col-span-6'
      },
      {
        type: 'sort',
        label: 'TVL',
        value: 'tvl',
        sortable: true,
        className: hasWalletAddress ? 'col-span-4' : 'col-span-5'
      },
      ...(hasWalletAddress
        ? ([
            {
              type: 'toggle',
              label: 'Holdings',
              value: HOLDINGS_TOGGLE_VALUE,
              className: 'col-span-4 justify-end',
              disabled: holdingsVaults.length === 0
            }
          ] satisfies TListHead['items'])
        : [])
    ]
  }

  return {
    refs: {
      varsRef,
      filtersRef
    },
    header: {
      vaultType: displayedVaultType,
      suggestedVaults
    },
    filtersBar: {
      search: {
        value: searchValue,
        onChange: onSearch
      },
      filters: {
        count: filtersCount,
        sections: filtersSections,
        config: filtersConfig,
        initialState: filtersInitialState,
        onApply: onApplyFilters,
        onClear: handleResetFilters
      },
      chains: {
        selected: displayedChains,
        onChange: handleChainsChange,
        config: chainConfig
      },
      shouldStackFilters,
      activeVaultType: displayedVaultType,
      onChangeVaultType: handleVaultVersionToggle
    },
    list: {
      listHeadProps,
      listVaultType,
      shouldCollapseChips,
      displayedShowStrategies,
      activeFilters: {
        activeChains,
        activeCategories,
        activeProductType,
        activeFeeStructureKey
      },
      data: {
        isLoading: isLoadingVaultList,
        pinnedSections,
        pinnedVaults,
        mainVaults,
        vaultFlags,
        listChains,
        totalMatchingVaults,
        hiddenByFiltersCount,
        blockingFilterActions
      },
      handlers: {
        onToggleChain: handleToggleChain,
        onToggleCategory: handleToggleCategory,
        onToggleType: handleToggleType,
        onToggleVaultType: handleToggleVaultType,
        onToggleFeeStructure: handleToggleFeeStructure
      },
      onResetFilters: handleResetFilters,
      resolveApyDisplayVariant
    }
  }
}
